/**
 * IndieChatBody
 * Center scrollable area: welcome screen (suggestion cards) or message thread.
 * Phase 1 additions:
 *   - react-markdown / remark-gfm rendering for assistant messages
 *   - Thumbs up / thumbs down feedback on each assistant message
 *   - Optional free-text follow-up after thumbs-down
 *   - Copy-rendered-text button
 *   - Non-dismissible QUOTA_EXCEEDED inline banner
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronRight, Check, Loader2, Send, ThumbsUp, ThumbsDown, Paperclip, Sparkles } from 'lucide-react';
import { ChatMessage, Prompt, SparkleIcon, suggestionCards } from './indie-chat.types';
import type { OrbitFeedbackPayload } from '../../services/OrbitChatService';
import type { OrbitArtifact } from '../../hooks/useOrbitArtifacts';
import {
  IndieMessageContext,
  MarkdownPre,
  MarkdownTable,
  MessageSave,
  SaveArtifactOverrides,
  Visibility,
  messageTables,
  messageTitle,
  pickArtifactFormat,
  sourceMessageId,
  startArtifactFileBuild,
  useAutoStoreCsv,
} from './IndieGeneratedFiles';

interface Props {
  messages: ChatMessage[];
  onSuggestionClick: (text: string) => void;
  /** Show streaming cursor on this message id */
  streamingMessageId?: string | null;
  isStreaming?: boolean;
  /** True while fetching a historic conversation from the server */
  isLoadingHistory?: boolean;
  /** Current context title shown in the welcome heading when no messages */
  context?: string;
  /** conversation_id for attaching feedback */
  conversationId?: string;
  /** Whether the daily/monthly token quota was exceeded */
  quotaExceeded?: boolean;
  /** Called when the user submits thumbs+optional text */
  onFeedback?: (payload: OrbitFeedbackPayload) => void;
  onSavePrompt?: (message: ChatMessage) => void;
  onSaveArtifact?: (message: ChatMessage, overrides?: SaveArtifactOverrides) => void | Promise<void>;
  /** Enables Excel / PDF / slides builds and auto-storing CSVs in Files */
  clientId?: number;
  /** Generated files inherit the conversation's visibility */
  conversationVisibility?: Visibility;
  artifacts?: OrbitArtifact[];
  surface?: 'default' | 'drawer';
  userName?: string;
  startPrompts?: Prompt[];
  hideStartPrompts?: boolean;
  disableSuggestionClicks?: boolean;
}

type RenderableSuggestionCard = {
  id: number;
  title: string;
  description: string;
  badge: string;
  icon: React.ReactNode;
  iconBg?: string;
  badgeBg?: string;
  badgeColor?: string;
};

// ── Per-message feedback state ────────────────────────────────────────────────

type FeedbackState = {
  thumbs: 1 | -1 | null;
  showInput: boolean;
  inputText: string;
  submitted: boolean;
};

const defaultFeedback = (): FeedbackState => ({
  thumbs: null, showInput: false, inputText: '', submitted: false,
});

const collectNodeText = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(collectNodeText).join(' ');
  if (React.isValidElement(node)) return collectNodeText(node.props.children);
  return '';
};

const ActionableOrderedList: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const items = React.Children.toArray(children).filter((child) => React.isValidElement(child));

  return (
    <div className="indie-actions-shell">
      <div className="indie-actions-header">
        <h2 className="indie-actions-title">Prioritized Actions</h2>
        <span className="indie-actions-count">{items.length} remaining</span>
      </div>
      <div className="indie-actions-list" role="list">
        {items.map((item, index) => {
          const content = React.isValidElement(item) ? item.props.children : item;
          if (!collectNodeText(content).trim()) return null;
          return (
            <div key={index} className="indie-action-row" role="listitem">
              <div className="indie-action-rank">{index + 1}</div>
              <div className="indie-action-content">{content}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const markdownComponents = {
  ol: ({ children }: { children?: React.ReactNode }) => <ActionableOrderedList>{children}</ActionableOrderedList>,
  pre: MarkdownPre,
  table: MarkdownTable,
};

type ArtifactFormat = 'xlsx' | 'pdf';
const FORMAT_NAMES: Record<ArtifactFormat, string> = { xlsx: 'Excel', pdf: 'PDF' };

/** Wider message column on big screens; drawers are narrower than all of these. */
const MESSAGE_COLUMN = 'max-w-2xl xl:max-w-[940px] 2xl:max-w-[1120px]';

// ── Component ─────────────────────────────────────────────────────────────────

const IndieChatBody: React.FC<Props> = ({
  messages,
  onSuggestionClick,
  streamingMessageId = null,
  isStreaming = false,
  isLoadingHistory = false,
  context = "Management One's AI advisor",
  conversationId,
  quotaExceeded = false,
  onFeedback,
  onSavePrompt,
  onSaveArtifact,
  artifacts = [],
  surface = 'default',
  userName,
  startPrompts,
  hideStartPrompts = false,
  disableSuggestionClicks = false,
  clientId,
  conversationVisibility,
}) => {
  const hasMessages = messages.length > 0;
  const isDrawerSurface = surface === 'drawer';
  const bottomRef = useRef<HTMLDivElement>(null);
  // Per-message feedback state keyed by message.id
  const [feedbackMap, setFeedbackMap] = useState<Record<string, FeedbackState>>({});
  // Per-message copy-button state
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // Per-message saved artifact state to prevent multiple saves (scoped by conversation)
  // value: true, or the file format the artifact was saved as
  const [savedArtifactIds, setSavedArtifactIds] = useState<Record<string, boolean | string>>({});
  // Per-message "Saving as Excel/PDF…" state and the open format picker
  const [savingFormats, setSavingFormats] = useState<Record<string, ArtifactFormat>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [formatMenuFor, setFormatMenuFor] = useState<string | null>(null);

  useEffect(() => {
    // Clean up any stale legacy global cache
    try {
      localStorage.removeItem('orbit_saved_artifact_message_ids');
    } catch {
      // ignore
    }

    if (!conversationId) {
      setSavedArtifactIds({});
      return;
    }
    try {
      const stored = localStorage.getItem(`orbit_saved_artifacts_${conversationId}`);
      if (stored) {
        setSavedArtifactIds(JSON.parse(stored) as Record<string, boolean | string>);
      } else {
        setSavedArtifactIds({});
      }
    } catch {
      setSavedArtifactIds({});
    }
  }, [conversationId]);

  const matchingArtifact = useCallback((msg: ChatMessage) => {
    if (conversationId && Array.isArray(artifacts) && artifacts.length > 0) {
      const msgIdStr = String(msg.id);
      const rawNumericId = msgIdStr.startsWith('hist-') ? msgIdStr.replace(/^hist-/, '') : msgIdStr;
      return artifacts.find((a) => {
        if (!a || a.conversation_id !== conversationId) {
          return false;
        }
        if (
          a.source_message_id != null &&
          (String(a.source_message_id) === String(msg.id) || String(a.source_message_id) === rawNumericId)
        ) {
          return true;
        }
        if (
          a.content_markdown &&
          msg.content &&
          msg.content.trim().length > 10 &&
          a.content_markdown.trim() === msg.content.trim()
        ) {
          return true;
        }
        return false;
      });
    }
    return undefined;
  }, [artifacts, conversationId]);

  const isMessageSaved = useCallback((msg: ChatMessage) => {
    if (!msg || !msg.id) return false;
    return Boolean(savedArtifactIds[msg.id] || savedArtifactIds[String(msg.id)] || matchingArtifact(msg));
  }, [matchingArtifact, savedArtifactIds]);

  /** 'xlsx' | 'pdf' when the saved artifact is file-backed, else null. */
  const savedFormat = useCallback((msg: ChatMessage): ArtifactFormat | null => {
    const local = savedArtifactIds[msg.id];
    if (local === 'xlsx' || local === 'pdf') return local;
    const type = matchingArtifact(msg)?.artifact_type;
    return type === 'excel' ? 'xlsx' : type === 'pdf' ? 'pdf' : null;
  }, [matchingArtifact, savedArtifactIds]);

  const persistSaved = useCallback((msgId: string, value: boolean | string | undefined) => {
    setSavedArtifactIds((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[msgId];
      else next[msgId] = value;
      if (conversationId) {
        try {
          localStorage.setItem(`orbit_saved_artifacts_${conversationId}`, JSON.stringify(next));
        } catch {
          // ignore
        }
      }
      return next;
    });
  }, [conversationId]);

  const handleSaveArtifactClick = useCallback(async (msg: ChatMessage, chosen?: ArtifactFormat) => {
    setFormatMenuFor(null);
    if (!msg || !msg.id || isMessageSaved(msg) || savingFormats[msg.id]) return;
    const setError = (text?: string) => setSaveErrors((prev) => {
      const next = { ...prev };
      if (text) next[msg.id] = text;
      else delete next[msg.id];
      return next;
    });
    setError();

    if (!clientId) {
      // no client: keep the plain markdown artifact
      persistSaved(msg.id, true);
      try {
        await onSaveArtifact?.(msg);
      } catch {
        persistSaved(msg.id, undefined);
        setError('Could not save this answer. Please try again.');
      }
      return;
    }

    // Build the file, then save the artifact pointing at it without waiting for the build.
    const format = chosen ?? pickArtifactFormat(msg);
    setSavingFormats((prev) => ({ ...prev, [msg.id]: format }));
    try {
      const title = messageTitle(msg);
      const sourceId = sourceMessageId(msg);
      const file = await startArtifactFileBuild({
        clientId,
        format,
        title,
        tables: format === 'xlsx' ? messageTables(msg) : undefined,
        summary: msg.content,
        fields: {
          ...(conversationId ? { conversation_id: conversationId } : {}),
          ...(sourceId ? { source_message_id: sourceId } : {}),
          ...(conversationVisibility ? { visibility: conversationVisibility } : {}),
        },
      });
      await onSaveArtifact?.(msg, {
        title,
        artifact_type: format === 'xlsx' ? 'excel' : 'pdf',
        content_markdown: msg.content,
        orbit_file_id: file.id,
      });
      persistSaved(msg.id, format);
    } catch {
      setError('Could not save this answer as a file. Please try again.');
    } finally {
      setSavingFormats((prev) => {
        const next = { ...prev };
        delete next[msg.id];
        return next;
      });
    }
  }, [clientId, conversationId, conversationVisibility, isMessageSaved, onSaveArtifact, persistSaved, savingFormats]);

  /** Save state + label for a message; its action row and its CSV card share it. */
  const messageSave = (msg: ChatMessage): MessageSave => {
    const saving = savingFormats[msg.id];
    const isSaved = isMessageSaved(msg);
    const doneFormat = isSaved ? savedFormat(msg) : null;
    return {
      state: saving ? 'saving' : isSaved ? 'saved' : 'idle',
      label: saving
        ? `Saving as ${FORMAT_NAMES[saving]}…`
        : isSaved ? (doneFormat ? `Saved as ${FORMAT_NAMES[doneFormat]}` : 'Artifact saved') : 'Save as artifact',
      error: saveErrors[msg.id] ?? null,
      run: () => void handleSaveArtifactClick(msg),
    };
  };

  const resolvedUserName = React.useMemo(() => {
    if (userName && typeof userName === 'string' && userName.trim()) {
      const clean = userName.trim();
      return clean.charAt(0).toUpperCase() + clean.slice(1);
    }
    const storageKeys = ['current_user', 'user', 'auth_user', 'retailorbit.user'];
    for (const key of storageKeys) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const parsed = JSON.parse(stored);
          const rawName = parsed?.first_name || parsed?.firstName || (parsed?.name ? parsed.name.split(' ')[0] : null);
          if (rawName && typeof rawName === 'string' && rawName.trim()) {
            const clean = rawName.trim();
            return clean.charAt(0).toUpperCase() + clean.slice(1);
          }
        }
      } catch {
        // ignore
      }
    }
    return '';
  }, [userName]);

  const resolvedSuggestionCards = React.useMemo<RenderableSuggestionCard[]>(() => {
    if (Array.isArray(startPrompts) && startPrompts.length > 0) {
      return startPrompts.map((prompt, index) => ({
        id: prompt.id || index + 1,
        title: prompt.label,
        description: '',
        badge: 'Prompt',
        icon: prompt.icon || <Sparkles className="w-4 h-4 text-[#7F56D9]" />,
        iconBg: prompt.iconBg || '#F4EBFF',
      }));
    }
    return suggestionCards;
  }, [startPrompts]);

  useAutoStoreCsv(messages, { clientId, conversationId, visibility: conversationVisibility, streamingMessageId });

  // Auto-scroll as tokens stream in
  useEffect(() => {
    if (isStreaming || messages.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages, isStreaming]);

  const getFeedback = (id: string): FeedbackState =>
    feedbackMap[id] ?? defaultFeedback();

  const setFeedback = (id: string, patch: Partial<FeedbackState>) =>
    setFeedbackMap((prev) => ({
      ...prev,
      [id]: { ...defaultFeedback(), ...(prev[id] ?? {}), ...patch },
    }));

  const handleThumb = useCallback(
    (msgId: string, thumbs: 1 | -1) => {
      const current = getFeedback(msgId);
      if (current.submitted) return; // already submitted — no toggling
      const showInput = thumbs === -1;
      setFeedback(msgId, { thumbs, showInput });
      if (thumbs === 1) {
        // Thumbs-up can be submitted immediately (no text needed)
        onFeedback?.({
          conversation_id: conversationId ?? '',
          thumbs,
        });
        setFeedback(msgId, { thumbs, showInput: false, submitted: true });
      }
    },
    [feedbackMap, conversationId, onFeedback],
  );

  const handleFeedbackSubmit = useCallback(
    (msgId: string) => {
      const fb = getFeedback(msgId);
      if (!fb.thumbs || fb.submitted) return;
      onFeedback?.({
        conversation_id: conversationId ?? '',
        thumbs: fb.thumbs,
        feedback_text: fb.inputText.trim() || undefined,
      });
      setFeedback(msgId, { submitted: true, showInput: false });
    },
    [feedbackMap, conversationId, onFeedback],
  );

  const handleCopy = useCallback((msgId: string, content: string) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopiedId(msgId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }, []);

  return (
    <div className="indie-chat flex-1 flex flex-col bg-white overflow-hidden min-w-0">

      {/* ── Quota exceeded banner (non-dismissible) ── */}
      {quotaExceeded && (
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 text-amber-800 text-[13px] flex items-center gap-2">
          <span className="text-lg" aria-hidden>⚠️</span>
          <span>
            <strong>Daily usage limit reached.</strong> Your AI message quota has been exceeded.
            Contact your advisor to continue or wait until tomorrow.
          </span>
        </div>
      )}

      <div className={`flex-1 flex flex-col ${isDrawerSurface ? 'items-start' : 'items-center'} overflow-y-auto scrollbar-thin px-4 relative ${!hasMessages && !isLoadingHistory ? (isDrawerSurface ? 'justify-start pt-6' : 'justify-center') : ''}`}>

        {/* ── Loading history skeleton ── */}
        {isLoadingHistory && !hasMessages && (
          <div className={`w-full ${MESSAGE_COLUMN} py-10 flex flex-col gap-4 animate-pulse`}>
            {[0.9, 0.7, 1, 0.75].map((w, i) => (
              <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="h-[38px] rounded-[14px] bg-[#EAECF0]"
                  style={{ width: `${w * 100}%`, maxWidth: i % 2 === 0 ? '72%' : '80%' }}
                />
              </div>
            ))}
            <div className="flex justify-center mt-4">
              <div className="flex items-center gap-2 text-[#98A2B3] text-xs">
                <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <span>Loading conversation…</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Decorative gradient (welcome state only) ── */}
        {!hasMessages && !isLoadingHistory && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]">
              <div
                className="absolute top-[10%] right-[10%] w-[240px] h-[300px] rounded-full opacity-[0.12]"
                style={{ background: '#89BCFF', filter: 'blur(120px)' }}
              />
              <div
                className="absolute bottom-[5%] left-[5%] w-[360px] h-[440px] rounded-full opacity-[0.10]"
                style={{ background: '#FF86E1', filter: 'blur(200px)' }}
              />
            </div>
          </div>
        )}

        {/* ── Welcome state ── */}
        {!hasMessages && !isLoadingHistory && (
          <div className={`relative z-10 flex flex-col ${isDrawerSurface ? 'items-start text-left' : 'items-center text-center'} w-full ${isDrawerSurface ? 'max-w-none px-1 pb-4' : 'max-w-3xl'}`}>
            {isDrawerSurface ? (
              <>
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#EEEDFE] border border-[#E0EAFF]/50 mb-3 shadow-xs">
                  <SparkleIcon stroke="#6366F1" className="w-5 h-5" />
                </div>
                <h1 className="font-geist text-[20px] font-semibold text-[#101828] leading-[28px] text-left mb-1 tracking-[-0.01em]">
                  {resolvedUserName ? `Hi ${resolvedUserName}, I'm Indie.` : "Hi, I'm Indie."}
                </h1>
                <p className="font-geist text-[13px] font-normal text-[#667085] leading-[20px] text-left max-w-full mb-3">
                  Your AI retail advisor. Ask me anything about your store &mdash; I know your numbers and can help you act on them.
                </p>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#D6BBFB] bg-[#F9F5FF] text-[11px] text-[#6941C6] font-medium mb-5 self-start">
                  <Sparkles className="w-3.5 h-3.5 text-[#6941C6]" />
                  <span>40 years of M1 expertise in one knowledge base</span>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center text-center mb-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EEEDFE] border border-[#E0EAFF]/50 mb-4 shadow-xs">
                  <SparkleIcon stroke="#6366F1" className="w-6 h-6" />
                </div>
                <h1 className="font-geist text-[26px] font-semibold text-[#101828] leading-[34px] text-center mb-2 tracking-[-0.01em]">
                  {resolvedUserName ? `Hi ${resolvedUserName}, I'm Indie.` : "Hi, I'm Indie."}
                </h1>
                <p className="font-geist text-[14px] font-normal text-[#667085] leading-[22px] text-center max-w-xl">
                  Your AI retail advisor. Ask me anything about your store &mdash; I know your numbers and can help you act on them.
                </p>
              </div>
            )}

            {!hideStartPrompts && (
              <div className={`w-full ${isDrawerSurface ? 'flex flex-col gap-2' : 'grid grid-cols-1 sm:grid-cols-2 gap-3'}`}>
                {resolvedSuggestionCards.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    disabled={disableSuggestionClicks}
                    onClick={() => onSuggestionClick(card.title)}
                    className={`flex items-center justify-between text-left transition-all group disabled:cursor-not-allowed disabled:opacity-50 ${isDrawerSurface
                      ? 'w-full px-3.5 py-3 rounded-xl border border-[#EAECF0] bg-white hover:border-[#D0D5DD] hover:shadow-xs'
                      : 'w-full p-4 rounded-xl border border-[#EAECF0] bg-white hover:border-[#D0D5DD] hover:shadow-xs'}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className={`flex-shrink-0 ${isDrawerSurface ? 'w-8 h-8 rounded-[8px]' : 'w-9 h-9 rounded-[9px]'} flex items-center justify-center`}
                        style={{ background: card.iconBg || '#F4EBFF' }}
                      >
                        {card.icon}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className={`font-geist font-medium text-[#101828] ${isDrawerSurface ? 'text-[13px] leading-[18px]' : 'text-[14px] leading-[20px]'} truncate`}>
                          {card.title}
                        </span>
                        {card.description && (
                          <span className="font-geist text-[12px] font-normal leading-[16px] text-[#667085] truncate mt-0.5">
                            {card.description}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full font-geist text-[11px] font-medium leading-[14px]"
                        style={{
                          background: card.badgeBg || '#F4EBFF',
                          color: card.badgeColor || '#6941C6',
                        }}
                      >
                        {card.badge}
                      </span>
                      <ChevronRight className="w-4 h-4 text-[#98A2B3]" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Message thread ── */}
        {hasMessages && (
          <div className={`relative z-10 w-full ${MESSAGE_COLUMN} flex flex-col gap-4 py-6`}>
            {messages.map((msg) => {
              const isActiveStream = msg.id === streamingMessageId;
              const isAssistant = msg.role === 'assistant';
              const fb = getFeedback(msg.id);
              const isCopied = copiedId === msg.id;
              const orbitErrorCode = isAssistant ? msg.metadata?.orbitErrorCode : undefined;
              const orbitErrorKind = isAssistant ? msg.metadata?.orbitErrorKind : undefined;
              const messageAttachments = msg.metadata?.attachments;
              const attachments = Array.isArray(messageAttachments) ? messageAttachments : [];

              return (
                <div
                  key={msg.id}
                  className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}
                >
                  <div className={`flex items-start gap-2.5 ${isAssistant ? 'w-full min-w-0' : 'justify-end max-w-[88%]'}`}>
                    {isAssistant && (
                      <div className="mt-1 w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-[linear-gradient(135deg,#3B82F6_0%,#7C3AED_100%)]">
                        <SparkleIcon stroke="white" />
                      </div>
                    )}
                    <div className={`flex flex-col gap-1 min-w-0 ${isAssistant ? 'flex-1' : ''}`}>
                      <div
                        data-orbit-error-code={orbitErrorCode}
                        data-orbit-error-kind={orbitErrorKind}
                        className={`indie-message-bubble text-[13px] leading-[1.65] ${isAssistant
                          ? 'text-[#101828] pt-0.5'
                          : 'indie-message-bubble-user px-3.5 py-2.5 rounded-[12px] border text-[#101828] border-[#E9D7FE]'
                          }`}
                      >
                        <p className="text-[10px] font-semibold mb-1 text-[#98A2B3] uppercase tracking-wider">
                          {isAssistant ? 'Indie' : 'You'}
                        </p>

                        {isAssistant ? (
                          /* Markdown rendering for assistant messages */
                          isActiveStream && !msg.content ? (
                            /* ── Typing dots — waiting for first token ── */
                            <span className="flex items-center gap-[5px] py-0.5" aria-label="Indie is thinking">
                              {[0, 1, 2].map((i) => (
                                <span
                                  key={i}
                                  className="w-2 h-2 rounded-full bg-[#7F56D9]"
                                  style={{
                                    animation: 'indie-typing-dot 1.2s ease-in-out infinite',
                                    animationDelay: `${i * 0.2}s`,
                                  }}
                                />
                              ))}
                            </span>
                          ) : (
                            <div className="indie-markdown max-w-none">
                              <IndieMessageContext.Provider
                                value={{
                                  message: msg, clientId, conversationId, visibility: conversationVisibility,
                                  ...(onSaveArtifact ? { save: messageSave(msg) } : {}),
                                }}
                              >
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                  {msg.content}
                                </ReactMarkdown>
                              </IndieMessageContext.Provider>
                              {isActiveStream && (
                                <span
                                  className="inline-block w-[2px] h-[13px] ml-0.5 bg-[#7F56D9] align-middle"
                                  style={{ animation: 'indie-pulse-dot 0.8s ease-in-out infinite' }}
                                  aria-hidden
                                />
                              )}
                            </div>
                          )
                        ) : (
                          /* Plain text for user messages */
                          <div className="flex flex-col gap-2">
                            <span className="whitespace-pre-wrap">{msg.content}</span>
                            {attachments.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 justify-end">
                                {attachments.map((attachment) => (
                                  <span
                                    key={attachment.attachmentId}
                                    title={`${attachment.attachmentId} · ${attachment.attachmentTypeDisplay}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-[#E9D7FE] bg-[#F9F5FF] px-2 py-1 text-[10px] font-medium text-[#6941C6]"
                                  >
                                    <Paperclip className="w-3 h-3" />
                                    <span className="font-semibold">{attachment.attachmentId}</span>
                                    <span className="opacity-90">{attachment.filename}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* ── Message action row ── */}
                      {!isActiveStream && msg.content && (
                        <div className="flex items-center gap-2 px-1 flex-wrap">
                          {isAssistant && (
                            <>
                              {(() => {
                                const { state, label, error } = messageSave(msg);
                                const isSaved = state === 'saved';
                                const saving = state === 'saving';
                                const canPick = Boolean(clientId) && !isSaved && !saving;
                                return (
                                  <span className="relative inline-flex items-center">
                                    <button
                                      onClick={() => void handleSaveArtifactClick(msg)}
                                      disabled={isSaved || Boolean(saving)}
                                      aria-label={label}
                                      className={`text-[11px] inline-flex items-center gap-1 transition-colors ${isSaved
                                        ? 'text-[#027A48] cursor-default font-medium'
                                        : 'text-[#6941C6] hover:text-[#53389E] disabled:cursor-wait'
                                        }`}
                                    >
                                      {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                                      {isSaved && <Check className="w-3 h-3 text-[#027A48]" />}
                                      {label}
                                    </button>
                                    {canPick && (
                                      <button
                                        type="button"
                                        onClick={() => setFormatMenuFor((open) => (open === msg.id ? null : msg.id))}
                                        aria-label="Choose artifact format"
                                        aria-haspopup="menu"
                                        aria-expanded={formatMenuFor === msg.id}
                                        className="ml-0.5 rounded p-0.5 text-[#6941C6] hover:bg-[#F9F5FF]"
                                      >
                                        <ChevronDown className="w-3 h-3" />
                                      </button>
                                    )}
                                    {canPick && formatMenuFor === msg.id && (
                                      <span role="menu" className="absolute left-0 top-full z-20 mt-1 flex w-36 flex-col rounded-[8px] border border-[#EAECF0] bg-white py-1 shadow-lg">
                                        {messageTables(msg).length > 0 && (
                                          <button type="button" role="menuitem" onClick={() => void handleSaveArtifactClick(msg, 'xlsx')} className="px-3 py-1.5 text-left text-[12px] text-[#344054] hover:bg-[#F9F5FF]">Save as Excel</button>
                                        )}
                                        <button type="button" role="menuitem" onClick={() => void handleSaveArtifactClick(msg, 'pdf')} className="px-3 py-1.5 text-left text-[12px] text-[#344054] hover:bg-[#F9F5FF]">Save as PDF</button>
                                      </span>
                                    )}
                                    {error && <span role="alert" className="ml-2 text-[11px] text-[#B42318]">{error}</span>}
                                  </span>
                                );
                              })()}
                              <button
                                onClick={() => handleCopy(msg.id, msg.content)}
                                aria-label="Copy response"
                                className="text-[11px] text-[#667085] hover:text-[#344054] inline-flex items-center gap-1"
                              >
                                {isCopied && <Check className="w-3 h-3 text-green-600" />}
                                {isCopied ? 'Copied' : 'Copy'}
                              </button>
                              <button
                                onClick={() => handleThumb(msg.id, 1)}
                                aria-label="Thumbs up"
                                title="Thumbs up"
                                className={`p-1 rounded transition-colors ${fb.thumbs === 1 ? 'text-[#7F56D9] bg-[#F4EBFF]' : 'text-[#667085] hover:text-[#7F56D9] hover:bg-[#F9F5FF]'}`}
                              >
                                <ThumbsUp className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleThumb(msg.id, -1)}
                                aria-label="Thumbs down"
                                title="Thumbs down"
                                className={`p-1 rounded transition-colors ${fb.thumbs === -1 ? 'text-[#F04438] bg-[#FEF3F2]' : 'text-[#667085] hover:text-[#F04438] hover:bg-[#FEF3F2]'}`}
                              >
                                <ThumbsDown className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}

                          {!isAssistant && (
                            <button
                              onClick={() => onSavePrompt?.(msg)}
                              aria-label="Save as prompt"
                              className="text-[11px] text-[#6941C6] hover:text-[#53389E]"
                            >
                              Save as prompt
                            </button>
                          )}
                        </div>
                      )}

                      {/* ── Thumbs-down free-text follow-up ── */}
                      {isAssistant && fb.showInput && !fb.submitted && (
                        <div className="flex items-center gap-2 mt-1 px-1">
                          <input
                            type="text"
                            value={fb.inputText}
                            onChange={(e) => setFeedback(msg.id, { inputText: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleFeedbackSubmit(msg.id);
                            }}
                            placeholder="What was wrong? (optional)"
                            className="flex-1 text-[12px] px-2 py-1 rounded border border-[#EAECF0] bg-white outline-none focus:border-[#7F56D9] text-[#101828]"
                            autoFocus
                          />
                          <button
                            onClick={() => handleFeedbackSubmit(msg.id)}
                            aria-label="Submit feedback"
                            className="p-1 rounded bg-[#7F56D9] text-white hover:bg-[#6941C6] transition-colors"
                          >
                            <Send className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => setFeedback(msg.id, { showInput: false, thumbs: null })}
                            className="text-[11px] text-[#98A2B3] hover:text-[#667085]"
                          >
                            skip
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Scroll anchor */}
            <div ref={bottomRef} />
          </div>
        )}
      </div>
    </div>
  );
};

export default IndieChatBody;
